import type React from "react";
import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Info24px } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Card, PixelPlaceholderLoader, Tooltip } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { DropdownReact } from "app/components/dropdown/dropdown-react";
import type {
    IDeviceDistributionCompareWidget,
    IWebRanksResponseData,
} from "app/pages/website-analysis/TrafficAndEngagement/types";
import { RankByMode } from "app/pages/website-analysis/TrafficAndEngagement/types";

import { CompareCardHeader } from "../common/CompareCardHeader";
import { DomainWithTooltipLink } from "../common/DomainWithTooltipLink";
import { EmptyStatePlaceholder } from "../common/EmptyStatePlaceholder";
import { useSubtitleFilterParams } from "../common/useSubtitleFilterParams";

export const WebsiteRank = ({
    subtitleFilters,
    data,
    onRankModeChange,
    selectedRankMode,
    isLoading,
}: IDeviceDistributionCompareWidget) => {
    const { translate } = useTranslate();

    const { countryCode, countryText, dateRangeText } = useSubtitleFilterParams(subtitleFilters);

    const categoryContent = useMemo(() => {
        if (!Array.isArray(subtitleFilters)) {
            return null;
        }

        const categoryFilter = (subtitleFilters as Array<Record<string, unknown>>).find(
            (f) => !f?.filter && typeof f?.value === "function",
        );

        if (!categoryFilter) {
            return null;
        }

        return (categoryFilter.value as () => React.ReactNode)();
    }, [subtitleFilters]);

    const typedData = data as IWebRanksResponseData | undefined;

    const rows = useMemo(() => {
        const rawData = typedData?.Data ?? [];

        return rawData.map((item) => ({
            domain: item.Domain,
            favicon: item.Favicon,
            rank: Number.isFinite(item.Rank) ? item.Rank : null,
        }));
    }, [typedData]);

    const rankItems = useMemo(
        () => [
            { id: RankByMode.Country, text: translate("wa.ao.ranks.dropdown.country") },
            { id: RankByMode.Global, text: translate("wa.ao.ranks.dropdown.global") },
            { id: RankByMode.Industry, text: translate("wa.ao.ranks.dropdown.category") },
        ],
        [translate],
    );

    const isEmpty = rows.length === 0;

    const titleAccessory = (
        <Flex
            alignItems="center"
            gap="8px"
        >
            <DropdownReact
                items={rankItems}
                onChange={onRankModeChange}
                selected={selectedRankMode}
                hasSearch={false}
                data-automation="website-rank-mode-dropdown"
            />
            <Tooltip content={translate("wa.ao.ranks.dropdown.tooltip")}>
                <Info24px
                    size={24}
                    color={colorsPalettes.carbon[200]}
                />
            </Tooltip>
        </Flex>
    );

    if (isLoading) {
        return (
            <Card data-automation="website-rank-compare-widget">
                <Flex
                    flexDirection="column"
                    padding="16px 24px"
                    gap="16px"
                    data-automation="website-rank-loading"
                >
                    <PixelPlaceholderLoader
                        width="40%"
                        height={16}
                    />
                    <PixelPlaceholderLoader
                        width="100%"
                        height={20}
                    />
                    <PixelPlaceholderLoader
                        width="100%"
                        height={20}
                    />
                </Flex>
            </Card>
        );
    }

    if (isEmpty) {
        return (
            <Card data-automation="website-rank-compare-widget">
                <CompareCardHeader
                    title={translate("wa.ao.ranks.dropdown")}
                    tooltipText={translate("wa.ao.ranks.dropdown.tooltip")}
                    dateRangeText={dateRangeText}
                    countryCode={countryCode}
                    countryText={countryText}
                    titleAccessory={titleAccessory}
                    additionalQueryContent={categoryContent}
                    dataAutomation="compare-website-rank"
                />
                <EmptyStatePlaceholder />
            </Card>
        );
    }

    return (
        <Card data-automation="website-rank-compare-widget">
            <CompareCardHeader
                title={translate("wa.ao.ranks.dropdown")}
                tooltipText={translate("wa.ao.ranks.dropdown.tooltip")}
                dataAutomation="compare-website-rank"
                dateRangeText={dateRangeText}
                countryCode={countryCode}
                countryText={countryText}
                titleAccessory={titleAccessory}
                additionalQueryContent={categoryContent}
            />
            <Flex
                flexDirection="column"
                gap="16px"
                padding="16px 24px"
                flex="1"
                data-automation="website-rank-table"
            >
                <Flex
                    alignItems="center"
                    justifyContent="space-between"
                    gap="12px"
                    data-automation="website-rank-table-header"
                >
                    <Text
                        fontVariant="captionTitle"
                        color={colorsPalettes.carbon[300]}
                    >
                        {translate("global.domain")}
                    </Text>
                    <Text
                        fontVariant="captionTitle"
                        color={colorsPalettes.carbon[300]}
                        textAlign="right"
                    >
                        {translate("common.rank")}
                    </Text>
                </Flex>
                {rows.map((row) => (
                    <Flex
                        key={row.domain}
                        alignItems="center"
                        justifyContent="space-between"
                        gap="12px"
                        data-automation="website-rank-row"
                    >
                        <Flex
                            flex="1"
                            minWidth="0"
                        >
                            <DomainWithTooltipLink
                                domain={row.domain}
                                favicon={row.favicon}
                            />
                        </Flex>
                        <Text
                            fontVariant="input2"
                            color={colorsPalettes.carbon[400]}
                            textAlign="right"
                            data-automation="website-rank-value"
                        >
                            {row.rank == null ? "N/A" : row.rank === 0 ? "-" : `#${row.rank}`}
                        </Text>
                    </Flex>
                ))}
            </Flex>
        </Card>
    );
};
